import React,{useState,useEffect,useCallback}from'react';
import Layout from'../components/layout/Layout';
import Badge from'../components/common/Badge';
import api from'../services/api';

const ACTION_COLOR:any={CREATE:'green',UPDATE:'blue',DELETE:'red',LOGIN:'gray',LOGOUT:'gray',VIEW:'blue',EXPORT:'orange'};

export default function AuditLogPage(){
  const [logs,setLogs]=useState<any[]>([]);
  const [sessions,setSessions]=useState<any[]>([]);
  const [alerts,setAlerts]=useState<any[]>([]);
  const [changes,setChanges]=useState<any[]>([]);
  const [changesLoading,setChangesLoading]=useState(false);
  const [changesLoaded,setChangesLoaded]=useState(false);
  const [tab,setTab]=useState('access');
  const [loading,setLoading]=useState(false);
  const [filters,setFilters]=useState({userId:'',action:'',module:'',dateFrom:new Date(Date.now()-7*24*3600000).toISOString().split('T')[0],dateTo:new Date().toISOString().split('T')[0]});
  const [page,setPage]=useState(0);
  const [total,setTotal]=useState(0);

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const[l,s,a]=await Promise.all([
        api.getAuditLogs?.({...filters,skip:page*50,take:50}).catch(()=>({data:{logs:[],total:0}})),
        api.getUserSessions?.().catch(()=>({data:[]})),
        api.getAuditAlerts?.().catch(()=>({data:[]})),
      ]);
      const logData=l?.data?.logs||l?.data?.data||l?.data||[];
      setLogs(Array.isArray(logData)?logData:[]);
      setTotal(l?.data?.total||logData.length||0);
      setSessions(Array.isArray(s?.data)?s.data:s?.data?.data||[]);
      setAlerts(Array.isArray(a?.data)?a.data:a?.data?.data||[]);
    }catch{}
    setLoading(false);
  },[filters,page]);

  const exportLogs=async()=>{
    try{await api.exportAuditLogs?.(filters);alert('Export started — check your email');}catch{}
  };

  const loadChangeLog=async()=>{
    setChangesLoading(true);
    try{
      const r=await api.getChangeLog?.({action:'UPDATE',take:50}).catch(()=>({data:{logs:[],total:0}}));
      const data=r?.data?.logs||r?.data?.data||r?.data||[];
      setChanges(Array.isArray(data)?data:[]);
    }catch{}
    setChangesLoaded(true);
    setChangesLoading(false);
  };

  useEffect(()=>{load();},[load]);

  return(
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div><h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1><p className="text-gray-500">System access and change audit trail</p></div>
          <button onClick={exportLogs} className="border px-4 py-2 rounded-lg hover:bg-gray-50 text-sm">Export CSV</button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <div><label className="block text-xs text-gray-500 mb-1">From</label><input type="date" value={filters.dateFrom} onChange={e=>setFilters(f=>({...f,dateFrom:e.target.value}))} className="w-full border rounded-lg p-2 text-sm"/></div>
            <div><label className="block text-xs text-gray-500 mb-1">To</label><input type="date" value={filters.dateTo} onChange={e=>setFilters(f=>({...f,dateTo:e.target.value}))} className="w-full border rounded-lg p-2 text-sm"/></div>
            <div><label className="block text-xs text-gray-500 mb-1">Action</label><select value={filters.action} onChange={e=>setFilters(f=>({...f,action:e.target.value}))} className="w-full border rounded-lg p-2 text-sm"><option value="">All</option>{['CREATE','UPDATE','DELETE','LOGIN','LOGOUT','VIEW','EXPORT'].map(a=><option key={a}>{a}</option>)}</select></div>
            <div><label className="block text-xs text-gray-500 mb-1">Module</label><select value={filters.module} onChange={e=>setFilters(f=>({...f,module:e.target.value}))} className="w-full border rounded-lg p-2 text-sm"><option value="">All Modules</option>{['PATIENT','IPD','LAB','PHARMACY','BILLING','SETTINGS','USER','EMR'].map(m=><option key={m}>{m}</option>)}</select></div>
            <div className="flex items-end"><button onClick={load} className="w-full bg-[#02357A] text-white rounded-lg py-2 text-sm hover:bg-[#015CA6]">{loading?'Loading…':'Search'}</button></div>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          {[['access',`Access Logs (${total})`],['sessions','User Sessions'],['alerts','Security Alerts'],['changes','Change History']].map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)} className={`px-5 py-2 rounded-full text-sm font-medium ${tab===k?'bg-[#02357A] text-white':'bg-white border text-gray-600 hover:border-blue-300'}`}>{l}</button>
          ))}
        </div>

        {tab==='access'&&(
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            {logs.length===0?<div className="p-12 text-center text-gray-400">{loading?'Loading audit logs…':'No logs for selected criteria'}</div>:(
              <>
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>{['Timestamp','User','IP Address','Action','Module','Resource','Details'].map(h=><th key={h} className="text-left p-3 text-gray-600 font-medium">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y">
                    {logs.map((log:any,i)=>(
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="p-3 text-xs text-gray-500">{log.created_at?.replace('T',' ').substring(0,19)||log.timestamp?.substring(0,19)||'—'}</td>
                        <td className="p-3"><p className="font-medium text-gray-900">{log.user_name||log.userName||log.user_email||'—'}</p><p className="text-xs text-gray-400">{log.role||'—'}</p></td>
                        <td className="p-3 text-xs font-mono">{log.ip_address||log.ipAddress||'—'}</td>
                        <td className="p-3"><Badge text={log.action||'VIEW'} color={ACTION_COLOR[log.action]||'gray'}/></td>
                        <td className="p-3 text-xs">{log.module||log.resource_type||'—'}</td>
                        <td className="p-3 text-xs text-gray-600">{log.resource_id||log.resourceId||'—'}</td>
                        <td className="p-3 text-xs text-gray-500 max-w-xs truncate">{log.details||log.description||log.changes?JSON.stringify(log.changes).substring(0,60):'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
</div>
                <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
                  <p className="text-sm text-gray-500">Showing {page*50+1}–{Math.min((page+1)*50,total)} of {total} records</p>
                  <div className="flex gap-2">
                    <button disabled={page===0} onClick={()=>setPage(p=>p-1)} className="border px-3 py-1.5 rounded-lg text-sm disabled:opacity-50">← Prev</button>
                    <button disabled={(page+1)*50>=total} onClick={()=>setPage(p=>p+1)} className="border px-3 py-1.5 rounded-lg text-sm disabled:opacity-50">Next →</button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {tab==='sessions'&&(
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full text-sm"><thead className="bg-gray-50"><tr>{['User','Login Time','Logout Time','IP','Branch','Station','Duration'].map(h=><th key={h} className="text-left p-3 text-gray-600 font-medium">{h}</th>)}</tr></thead>
              <tbody className="divide-y">{sessions.length===0?<tr><td colSpan={7} className="p-8 text-center text-gray-400">No active sessions</td></tr>:
                sessions.map((s:any,i)=><tr key={i} className="hover:bg-gray-50">
                  <td className="p-3"><p className="font-medium">{s.user_name||s.userName||'—'}</p><p className="text-xs text-gray-400">{s.user_email||s.email||'—'}</p></td>
                  <td className="p-3 text-xs">{s.login_at||s.loginAt||'—'}</td>
                  <td className="p-3 text-xs">{s.logout_at||s.logoutAt||<Badge text="ACTIVE" color="green"/>}</td>
                  <td className="p-3 text-xs font-mono">{s.ip_address||'—'}</td>
                  <td className="p-3 text-xs">{s.branch_name||'—'}</td>
                  <td className="p-3 text-xs">{s.station_name||'—'}</td>
                  <td className="p-3 text-xs">{s.duration_minutes?`${s.duration_minutes}m`:'Active'}</td>
                </tr>)
              }</tbody>
            </table>
          </div>
        )}

        {tab==='alerts'&&(
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full text-sm"><thead className="bg-gray-50"><tr>{['Time','Alert Type','User','Details','Status'].map(h=><th key={h} className="text-left p-3 text-gray-600 font-medium">{h}</th>)}</tr></thead>
              <tbody className="divide-y">{alerts.length===0?<tr><td colSpan={5} className="p-8 text-center text-gray-400">No security alerts</td></tr>:
                alerts.map((a:any,i)=><tr key={i} className="hover:bg-red-50">
                  <td className="p-3 text-xs">{a.created_at?.substring(0,16)||'—'}</td>
                  <td className="p-3"><Badge text={a.alert_type||a.alertType||'SECURITY'} color="red"/></td>
                  <td className="p-3">{a.user_name||'—'}</td>
                  <td className="p-3 text-xs text-gray-600">{a.details||a.description||'—'}</td>
                  <td className="p-3"><Badge text={a.status||'OPEN'} color={a.status==='RESOLVED'?'green':'red'}/></td>
                </tr>)
              }</tbody>
            </table>
          </div>
        )}

        {tab==='changes'&&(
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            {!changesLoaded?(
              <div className="p-8 text-center text-gray-400">
                <div className="text-4xl mb-3">📝</div>
                <p>Change history shows before/after values for all patient and clinical record modifications</p>
                <button onClick={loadChangeLog} disabled={changesLoading} className="mt-4 bg-[#02357A] text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">
                  {changesLoading?'Loading…':'Load Change Log'}
                </button>
              </div>
            ):(
              <table className="w-full text-sm"><thead className="bg-gray-50"><tr>{['Time','User','Module','Action','Resource','Details'].map(h=><th key={h} className="text-left p-3 text-gray-600 font-medium">{h}</th>)}</tr></thead>
                <tbody className="divide-y">{changes.length===0?<tr><td colSpan={6} className="p-8 text-center text-gray-400">No changes recorded</td></tr>:
                  changes.map((c:any,i)=><tr key={c.id||i} className="hover:bg-gray-50">
                    <td className="p-3 text-xs">{(c.created_at||c.createdAt||'').toString().substring(0,16)||'—'}</td>
                    <td className="p-3 text-xs">{c.user_email||c.userEmail||'—'}</td>
                    <td className="p-3 text-xs">{c.module||'—'}</td>
                    <td className="p-3"><Badge text={c.action||'—'} color={ACTION_COLOR[c.action]||'gray'}/></td>
                    <td className="p-3 text-xs font-mono">{c.resource_id||c.resourceId||'—'}</td>
                    <td className="p-3 text-xs text-gray-600 max-w-xs truncate">{typeof c.details==='object'?JSON.stringify(c.details):(c.details||'—')}</td>
                  </tr>)
                }</tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
